import { motion } from 'framer-motion';
import { Database, BarChart3 } from 'lucide-react';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const DataSourcesStatusPanel = ({ coverage, scannedAt, itemsScanned }) => {
  const { theme } = useTheme();
  const currentColors = colors[theme];

  const coverageBars = coverage
    ? [
        { label: 'Mapping', value: coverage.mapping_status?.mapped || 0, color: currentColors.accentGreen },
        { label: 'SKU Size', value: coverage.sku_size_present || 0, color: currentColors.accentBlue },
        { label: 'Unit Norm', value: coverage.unit_norm_present || 0, color: currentColors.accentPurple },
        { label: 'Category', value: coverage.category_present || 0, color: currentColors.accentOrange },
      ]
    : [];

  return (
    <motion.div
      className="rounded-xl p-4 mb-6"
      style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      data-testid="data-sources-status-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Database size={18} style={{ color: currentColors.accentBlue }} />
        <h3 className="font-semibold text-sm" style={{ color: currentColors.textPrimary }}>
          Data Sources & Coverage
        </h3>
      </div>

      {/* Connection Status */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs">
        {itemsScanned != null && (
          <span className="flex items-center gap-1.5" style={{ color: currentColors.textSecondary }}>
            <BarChart3 size={12} /> Items Scanned: <strong>{itemsScanned}</strong>
          </span>
        )}
      </div>

      {/* Coverage Bars */}
      {coverageBars.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {coverageBars.map((bar) => (
            <div key={bar.label} data-testid={`coverage-${bar.label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: currentColors.textSecondary }}>{bar.label}</span>
                <span className="font-medium" style={{ color: currentColors.textPrimary }}>
                  {Math.round(bar.value * 100)}%
                </span>
              </div>
              <div
                className="h-1.5 rounded-full overflow-hidden"
                style={{ background: currentColors.background }}
              >
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: bar.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${bar.value * 100}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {scannedAt && (
        <p className="mt-3 text-xs" style={{ color: currentColors.textSecondary }}>
          Last scanned: {new Date(scannedAt).toLocaleString()}
        </p>
      )}
    </motion.div>
  );
};

export default DataSourcesStatusPanel;
